import React from 'react';
import { plainDate, plainDayBefore } from '../utils/plainDate';
import './PoDeliveryChanges.css';

const count = (value) => Number.isInteger(value) ? value.toLocaleString('en-US') : 'Unavailable';
const percent = (value) => typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)}%` : null;
const ready = (data) => ['ready', 'no_received_pos'].includes(data?.availability) && data?.summary;
// The owner's one plain line about receipts left out of the counts (unmatched to the
// change log, or newer than it). Absent when nothing was left out.
const leftOut = (data) => (typeof data?.coverage?.left_out_note === 'string' ? data.coverage.left_out_note : null);
// The received window in plain dates (Lane 1382, Ryan: no ISO dates on screen). The API
// sends the start and the exclusive end; the end shown is the window's last day.
const windowText = (w) => {
  const start = plainDate(w?.start);
  const last = plainDayBefore(w?.end_exclusive);
  return start && last ? `POs received from ${start} through ${last}.` : 'POs received in the selected window.';
};
// No PO change history was ever loaded for this company: the section shows nothing.
const absent = (data) => !data || data.availability === 'no_history';

export function PoDeliveryFigures({ data }) {
  if (absent(data)) return null;
  if (!ready(data)) return <p className="po-delivery-changes po-delivery-note" role="status">Delivery date history is unavailable for this period.</p>;
  const s = data.summary;
  const total = s.total_received_po_count;
  // Ryan 2026-10-02: the share is the big number, the count of the total beside it.
  const metrics = [
    ['Unique POs received', count(total), null],
    ['POs with a delivery date change', percent(s.changed_po_percentage) || count(s.changed_po_count),
     `${count(s.changed_po_count)} of ${count(total)}`],
    ['POs with dates changed more than once', percent(s.repeated_po_percentage) || count(s.repeated_po_count),
     `${count(s.repeated_po_count)} of ${count(total)}`],
  ];
  return <section className="po-delivery-changes" aria-label="Delivery date changes">
    <div className="po-delivery-figures">
      {metrics.map(([label, big, small]) => <div className="po-delivery-figure" key={label}>
        <div className="po-delivery-label">{label}</div>
        <div className="po-delivery-value">{big}{small && <span>{small}</span>}</div>
      </div>)}
    </div>
    <p className="po-delivery-note">{windowText(data.window)} A PO counts as changed if its date ever moved in the loaded history.{leftOut(data) ? ` ${leftOut(data)}` : ''}</p>
  </section>;
}

export function PoDeliveryBands({ data }) {
  if (absent(data) || !ready(data)) return null;
  const bands = data.net_bands || [];
  const c = data.coverage || {};
  return <section className="po-delivery-changes po-delivery-bands" aria-label="How far delivery dates moved">
    <div className="po-delivery-bands-title">How far delivery dates moved</div>
    <div className="po-delivery-bands-scroll">
      <table className="po-delivery-bands-table">
        <thead><tr>{bands.map((band) => <th scope="col" key={band.code}>{band.label}</th>)}</tr></thead>
        <tbody><tr>{bands.map((band) => <td key={band.code}>{percent(band.percentage) || '-'}</td>)}</tr></tbody>
      </table>
    </div>
    <p className="po-delivery-note">How far each PO's delivery date moved, from its first date to its last. Each PO counted once. Receipts through {plainDate(c.receipt_date_max) || 'unknown'}; changes through {plainDate(c.log_action_at_max) || 'unknown'}.</p>
  </section>;
}
